import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { EmrCheck } from "@medstonetech/slate-icons";
import { useYupValidationResolver } from "hooks";
import {
  useGenerateCustomSummaryDocument,
  UseGenerateCustomSummaryDocumentPayload,
} from "modules/charts-shared/api";
import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Textarea, WarningDialog } from "shared";
import { enumMapper, extractApiErrorMessage, isLastIndex } from "utils";
import { SummaryReasons } from "enums";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import * as yup from "yup";
import { SummaryForm } from "./Summary";
import { useReturnToSelectedChart } from "./useReturnToSelectedChart";

const CHART_SUMMARY_SCHEMA = yup.object({
  selectedSections: yup.array(),
  miscFilesToAdd: yup.array(),
  selectedProviderCharts: yup.array(),
  selectedNurseCharts: yup.array(),
});

function ChartSummary() {
  const {
    encounterId = "",
    chartType = "",
    chartCode = "",
  } = useParams<{
    encounterId: string;
    chartType: string;
    chartCode: string;
  }>();

  const [reason, setReason] = React.useState<SummaryReasons | undefined>(
    undefined
  );
  const [otherReason, setOtherReason] = React.useState<string | undefined>(
    undefined
  );

  const toast = useToast();
  const navigate = useNavigate();
  const yupResolver = useYupValidationResolver(CHART_SUMMARY_SCHEMA);

  const form = useForm<SummaryForm>({
    mode: "onSubmit",
    defaultValues: {
      selectedSections: [],
      miscFilesToAdd: [],
      selectedProviderCharts: [],
      selectedNurseCharts: [],
    },
    resolver: yupResolver,
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = form;

  const { mutateAsync: generateSummaryDocument, isLoading: isLoadingDocument } =
    useGenerateCustomSummaryDocument(encounterId || "", reason, otherReason);

  const onSubmit: SubmitHandler<SummaryForm> = async (data: SummaryForm) => {
    try {
      const payload: UseGenerateCustomSummaryDocumentPayload = {
        addPatientDemographics: false,
        addRegistrationDocuments: false,
        addInsuranceInformation: false,
        addNursingChartSummary: Boolean(data.selectedNurseCharts?.length),
        addProviderChartSummary: Boolean(data.selectedProviderCharts?.length),
        addLabReports: false,
        addRadiologyReports: false,
        addEkgReports: false,
        addDischargeFile: false,
        addObservationFile: false,
        addMedicationsReport: false,
        miscFilesToAdd: data.miscFilesToAdd,
        charts: [
          ...(data.selectedNurseCharts ?? []),
          ...(data.selectedProviderCharts ?? []),
        ],
      };

      await generateSummaryDocument(payload);

      const mainRoute = window.location.hash.split("/")[1];
      navigate(
        `/${mainRoute}/charts/${encounterId}/${chartType}/${chartCode}/patient-info`,
        {
          state: {
            fromRoute: `/${mainRoute}/search`,
            fromLabel: "Back",
            isPastEncounter: true,
          },
        }
      );
    } catch (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  };

  const onChangeReason = (
    value: SummaryReasons,
    description: string | undefined
  ) => {
    setReason(value);
    setOtherReason(description);

    handleSubmit(onSubmit)();
  };

  return (
    <SummaryReasonModal
      isOpen
      onChangeReason={onChangeReason}
      isLoadingDocument={isLoadingDocument}
      isValid={isValid}
    />
  );
}

type SummaryReasonModalProps = {
  isOpen: boolean;
  isLoadingDocument?: boolean;
  isValid?: boolean;
  onChangeReason: (
    reason: SummaryReasons,
    description: string | undefined
  ) => void;
};

function SummaryReasonModal({
  isOpen,
  isLoadingDocument = false,
  isValid = true,
  onChangeReason,
}: SummaryReasonModalProps) {
  const [selectedReason, setSelectedReason] = useState<
    SummaryReasons | undefined
  >(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);

  const reasons = enumMapper.getOptions("summaryReasons");

  const returnToSelectedChart = useReturnToSelectedChart();

  const warningDisclosure = useDisclosure();

  const reasonsFiltered = reasons.filter(
    (reason) => reason.value !== "SummaryReasonOther"
  );

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered>
      <ModalOverlay />
      <ModalContent
        zIndex={1000}
        borderRadius="md"
        bgColor="gray.50"
        minHeight="480px"
        width="510px"
        maxWidth="510px"
      >
        <ModalHeader borderTopRadius="md">
          <Flex justifyContent="space-between" alignItems="center">
            <Button
              variant="label"
              color="blue"
              onClick={() => {
                returnToSelectedChart();
              }}
            >
              Cancel
            </Button>
            <Box>Chart Record</Box>
            <Button
              variant="label"
              disabled={!isValid || !selectedReason || isLoadingDocument}
              isLoading={isLoadingDocument}
              color="blue"
              onClick={() => {
                if (
                  selectedReason === "SummaryReasonOther" &&
                  (description?.trim().length ?? 0) < 6
                ) {
                  warningDisclosure.onOpen();
                  return;
                }

                if (selectedReason) {
                  onChangeReason(selectedReason, description);
                }
              }}
            >
              Generate
            </Button>
            <WarningDialog
              isOpen={warningDisclosure.isOpen}
              mainText="You must enter a reason to continue."
              title="Warning!"
              actionLabel="OK"
              onlyActionButton
              actionButtonProps={{ color: "blue" }}
              onCancel={warningDisclosure.onClose}
              onAction={warningDisclosure.onClose}
              onClose={warningDisclosure.onClose}
            />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Box textAlign="center" pb={6}>
            Select the Reason you are accessing this <br /> Chart Record.
          </Box>
          <Box
            borderRadius="10px"
            bg="white"
            overflow="hidden"
            outline="none"
            minH="50px"
            mb={2}
          >
            {reasonsFiltered.map((item, index) => (
              <>
                <Box
                  display="flex"
                  px={4}
                  py={4}
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => {
                    setSelectedReason(item.value);
                  }}
                  cursor="pointer"
                >
                  <Box fontWeight="600" pl={2}>
                    {item.label}
                  </Box>
                  {selectedReason === item.value && (
                    <Box h="24px" w="24px" pt={1}>
                      <Icon
                        h="14px"
                        w="14px"
                        as={EmrCheck}
                        color="blue"
                        verticalAlign="top"
                      />
                    </Box>
                  )}
                </Box>
                {!isLastIndex(index, reasonsFiltered) && (
                  <Divider
                    width={`calc(100% - 20px)`}
                    ml="20px"
                    mt={1}
                    h="0.5px"
                  />
                )}
              </>
            ))}
          </Box>
          {selectedReason === "SummaryReasonOther" && (
            <Box
              borderRadius="10px"
              bg="white"
              overflow="hidden"
              outline="none"
              minH="50px"
              p={1}
              mt={2}
              mb={2}
            >
              <Textarea
                placeholder="Type other reason here..."
                size="sm"
                resize="none"
                onChange={(e) => {
                  setDescription(e.currentTarget.value);
                }}
              />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export { ChartSummary };
